/** Keys of the `site_settings` table. Public keys are exposed by GET /site/settings/public. */

export const SETTING_KEYS = {
  whatsappNumber: 'contact.whatsappNumber',
  phone: 'contact.phone',
  email: 'contact.email',
  address: 'contact.address',
  hours: 'contact.hours',
  mapEmbedUrl: 'contact.mapEmbedUrl',
  social: 'social.links',
  featuredProductIds: 'home.featuredProductIds',
  /** The YouTube video on the home page (D-333): a normalised `https://www.youtube.com/watch?v=ID` address, or `null`. */
  homeVideoUrl: 'home.videoUrl',
  flagNewsletter: 'flags.newsletter',
  /** `true` once a Super Admin has confirmed the go-live (D-382); until then every public page carries the red testing strip. */
  siteLive: 'site.live',
  twoFactorRequiredRoles: 'auth.twoFactorRequiredRoles',
  deletionGraceDays: 'account.deletionGraceDays',
} as const;
export type SettingKey = (typeof SETTING_KEYS)[keyof typeof SETTING_KEYS];

/** Exposed to anonymous visitors. */
export const PUBLIC_SETTING_KEYS: readonly SettingKey[] = [
  SETTING_KEYS.whatsappNumber,
  SETTING_KEYS.phone,
  SETTING_KEYS.email,
  SETTING_KEYS.address,
  SETTING_KEYS.hours,
  SETTING_KEYS.mapEmbedUrl,
  SETTING_KEYS.social,
  SETTING_KEYS.featuredProductIds,
  SETTING_KEYS.homeVideoUrl,
  SETTING_KEYS.flagNewsletter,
  SETTING_KEYS.siteLive,
];

/**
 * The business details the owner supplied (D-315, D-318): the WhatsApp / call number, the e-mail address, the
 * address, the map of the works and — until the real profiles exist — the standard address of each social
 * network. They are the defaults of a key that has no row yet, and the values the seed and `db:bootstrap` write
 * — the Super Admin edits them under Settings afterwards. Everything the owner has not supplied is an explicit
 * placeholder or simply absent (docs/CONTENT_TODO.md): there are no opening hours.
 */
export const BUSINESS_WHATSAPP_NUMBER = '919024780751';
export const BUSINESS_PHONE = '+91-9024780751';
export const BUSINESS_EMAIL = 'sbgindiarj@gmail.com';
/** Instagram, Facebook and YouTube — the networks the site shows (no LinkedIn); the home page of each for now. */
export const BUSINESS_SOCIAL: Readonly<Record<string, string>> = {
  instagram: 'https://www.instagram.com/',
  facebook: 'https://www.facebook.com/',
  youtube: 'https://www.youtube.com/',
};
export const BUSINESS_ADDRESS =
  'M/s Shree Balaji Granites\nE-37, RIICO Industrial Area,\nBaggad, Baggad-Bhim,\nRajsamand 313341,\nRajasthan, India';
/**
 * What the address was before 2026-10-05 (D-323). A database that still holds exactly this text — the old default,
 * never edited — is moved to the new one by a migration; an address somebody wrote themselves is left alone.
 */
export const BUSINESS_ADDRESS_BEFORE_D323 =
  'M/s Shree Balaji Granites\nE 37 RIICO Industrial Area Baggad,\nBaggad-Bhim, Rajsamand 313341,\nRajasthan';
/** The `src` of the Google Maps embed of the works (searched as 25°35'15.4"N 73°53'31.6"E). */
export const BUSINESS_MAP_EMBED_URL =
  'https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3598.5209283979293!2d73.88953777539459!3d25.587600677461676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjXCsDM1JzE1LjQiTiA3M8KwNTMnMzEuNiJF!5e0!3m2!1sen!2sin!4v1791116615619!5m2!1sen!2sin';

export const DEFAULT_SETTINGS: Readonly<Record<SettingKey, unknown>> = {
  [SETTING_KEYS.whatsappNumber]: BUSINESS_WHATSAPP_NUMBER,
  [SETTING_KEYS.phone]: BUSINESS_PHONE,
  [SETTING_KEYS.email]: BUSINESS_EMAIL,
  [SETTING_KEYS.address]: BUSINESS_ADDRESS,
  [SETTING_KEYS.hours]: null,
  [SETTING_KEYS.mapEmbedUrl]: BUSINESS_MAP_EMBED_URL,
  [SETTING_KEYS.social]: { ...BUSINESS_SOCIAL },
  [SETTING_KEYS.featuredProductIds]: [],
  // never a made-up video: the section stays hidden until an admin pastes a link (docs/CONTENT_TODO.md)
  [SETTING_KEYS.homeVideoUrl]: null,
  [SETTING_KEYS.flagNewsletter]: true,
  // a new site starts in testing; only the Super Admin's go-live confirmation turns it on (never seeded or reset to true)
  [SETTING_KEYS.siteLive]: false,
  [SETTING_KEYS.twoFactorRequiredRoles]: ['SUPER_ADMIN'],
  [SETTING_KEYS.deletionGraceDays]: 7,
};
